/**
 * "Continue with Microsoft" (ENG-6169): opens Microsoft's sign-in popup through
 * MSAL and hands over the ID token. MSAL loads when the button mounts and the
 * button stays disabled until it is ready, so every click opens the popup at
 * once, before a browser would block it. If MSAL fails to load, nothing shows:
 * the form still works.
 */
import { useEffect, useRef, useState } from 'react';

import {
  type MicrosoftIdentity,
  MicrosoftSignInCancelled,
  loadMicrosoftIdentity,
} from '@/auth/utils/microsoftIdentity';
import Button from '@/componentsv2/Button';
import config from '@/config';

import MicrosoftLogo from './MicrosoftLogo';

const LABELS = {
  continue_with: 'Continue with Microsoft',
  signup_with: 'Sign up with Microsoft',
};

export interface MicrosoftSignInButtonProps {
  text: 'continue_with' | 'signup_with';
  /** Receives the Microsoft ID token. A credential: never log it. */
  onCredential: (idToken: string) => void;
  /** A popup or MSAL failure. A closed or blocked popup is not one. */
  onError: (err: unknown) => void;
  /** Disables the button and drops any credential that arrives meanwhile. */
  disabled?: boolean;
  /** The Entra app's client id; a seam for stories and tests. */
  clientId?: string;
  /** Loads MSAL; a seam for stories and tests. */
  load?: (clientId: string) => Promise<MicrosoftIdentity>;
  /** Told when MSAL failed to load and the button is gone. */
  onLoadFailed?: () => void;
}

export default function MicrosoftSignInButton({
  text,
  onCredential,
  onError,
  disabled = false,
  clientId = config.microsoftClientId,
  load = loadMicrosoftIdentity,
  onLoadFailed,
}: MicrosoftSignInButtonProps): JSX.Element | null {
  const [identity, setIdentity] = useState<MicrosoftIdentity>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const loadFailedHandler = useRef(onLoadFailed);
  const mounted = useRef(true);
  // The popup outlives renders: these refs let it reach the current handlers
  // and the current disabled state, not the ones of the render that opened it.
  const credentialHandler = useRef(onCredential);
  const errorHandler = useRef(onError);
  const isDisabled = useRef(disabled);
  useEffect(() => {
    credentialHandler.current = onCredential;
    errorHandler.current = onError;
    loadFailedHandler.current = onLoadFailed;
    isDisabled.current = disabled;
  }, [onCredential, onError, onLoadFailed, disabled]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setIdentity(undefined);
    setLoadFailed(false);
    load(clientId)
      .then((loaded) => {
        if (!cancelled) setIdentity(loaded);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        loadFailedHandler.current?.();
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, load]);

  const signIn = async () => {
    if (!identity) return;
    setPopupOpen(true);
    try {
      // Straight from the click, so the popup opens at once.
      const idToken = await identity.signIn();
      // A popup that finishes after the page moved on, or while it is busy,
      // delivers nothing.
      if (mounted.current && !isDisabled.current) credentialHandler.current(idToken);
    } catch (err) {
      if (mounted.current && !(err instanceof MicrosoftSignInCancelled)) {
        errorHandler.current(err);
      }
    } finally {
      if (mounted.current) setPopupOpen(false);
    }
  };

  if (loadFailed) return null;

  return (
    <Button
      variant="secondary"
      size="medium"
      className="w-full justify-center"
      startIcon={MicrosoftLogo}
      disabled={disabled || popupOpen || !identity}
      onClick={signIn}
      data-testid="microsoft-sign-in-button"
    >
      {LABELS[text]}
    </Button>
  );
}
